import type {
  Activity,
  ActivityType,
  AcknowledgementPayloadDTO,
  AcknowledgementResponseDTO,
  ConsentPayloadDTO,
  ConsentResponseDTO,
  PatientProfile,
  ProfileUpdatePayload,
  UUID,
} from "@/core/types";
import type { ApiRequest, ApiRequestWith } from "@/api/types";
import { httpAuth2 } from "@/api/auth";
import type { InsuranceListResponse } from "@/features/insurance/types";

type PatientRequestsTyped = {
  append_acknowledgement: ApiRequestWith<
    AcknowledgementResponseDTO,
    { body: AcknowledgementPayloadDTO }
  >;
  append_patient_consent: ApiRequestWith<ConsentResponseDTO, { body: ConsentPayloadDTO }>;
  fetch_profile: ApiRequest<{ user: PatientProfile }>;
  get_activity_list: ApiRequestWith<Activity[], { searchParams: { type: ActivityType } }>;
  get_insurance_list: ApiRequest<InsuranceListResponse>;
  remove_from_wait_list: ApiRequestWith<void, { searchParams: { patient_username: UUID } }>;
  update_profile: ApiRequestWith<PatientProfile, { body: ProfileUpdatePayload }>;
};

export const patientRequestsTyped: PatientRequestsTyped = {
  append_acknowledgement: function ({ body, context }) {
    return httpAuth2
      .post("/v1/patient/append_acknowledgement", { context, json: body })
      .json<AcknowledgementResponseDTO>();
  },

  append_patient_consent: function ({ body, context }) {
    return httpAuth2
      .post("/v1/patient/append_consent", {
        context: { preventAllErrorToast: true, ...context },
        json: body,
      })
      .json<ConsentResponseDTO>();
  },

  fetch_profile: function ({ context } = {}) {
    return httpAuth2.post("/v1/patient/profile", { context }).json<{ user: PatientProfile }>();
  },

  get_activity_list: function ({ context, searchParams }) {
    return httpAuth2.post("/v1/patient/activity", { context, searchParams }).json<Activity[]>();
  },

  // intentionally authenticated — post-login counterpart to fetch_insurance_list in patient.js
  // (same endpoint, different auth level by design)
  get_insurance_list: function ({ context } = {}) {
    return httpAuth2.post("/v2/patient/insurance", { context }).json<InsuranceListResponse>();
  },

  remove_from_wait_list: function ({ context, searchParams }) {
    return httpAuth2
      .post("/v1/patient/wait_list", {
        context,
        searchParams: { ...searchParams, method: "remove" },
      })
      .json<void>();
  },

  update_profile: function ({ body, context }) {
    return httpAuth2
      .post("/v1/patient/update_profile", { context, json: body })
      .json<PatientProfile>();
  },
};
